import { useCallback, useEffect, useState } from 'react';
import { KeyRound, Plus, Edit2, Trash2, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { Input } from '../../components/ui/Input';
import { InlineConfirmation } from '../../components/ui/InlineConfirmation';
import { Skeleton } from '../../components/ui/Skeleton';
import { PageHeader } from '../../components/ui/PageHeader';
import { apiClient } from '../../lib/api-client';
import { usePermission } from '../../hooks/usePermission';

type ProviderType = 'azure-entra' | 'oidc' | 'saml';

interface IdpRow {
  alias: string;
  displayName: string;
  providerType: ProviderType;
  enabled: boolean;
  tenantId?: string;
  clientId?: string;
  clientSecret?: string;
  discoveryUrl?: string;
}

interface IdpFormState {
  alias: string;
  displayName: string;
  providerType: ProviderType;
  tenantId: string;
  clientId: string;
  clientSecret: string;
  discoveryUrl: string;
  enabled: boolean;
}

const emptyForm = (): IdpFormState => ({
  alias: '',
  displayName: '',
  providerType: 'azure-entra',
  tenantId: ':portal-azure-tenant-id',
  clientId: ':portal-azure-client-id',
  clientSecret: ':portal-azure-client-secret',
  discoveryUrl: '',
  enabled: true,
});

const vaultRefHint = 'Vault reference, e.g. :portal-azure-client-id';

export function IdentityProviders() {
  const { isAllowed: canView } = usePermission('user_groups', 'read');
  const { isAllowed: canManage } = usePermission('user_groups', 'manage_members');
  const [idps, setIdps] = useState<IdpRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAlias, setEditingAlias] = useState<string | null>(null);
  const [form, setForm] = useState<IdpFormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmDeleteAlias, setConfirmDeleteAlias] = useState<string | null>(null);

  const fetchIdps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.getIdentityProviders();
      setIdps(res.idps || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load identity providers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) void fetchIdps();
  }, [canView, fetchIdps]);

  const openCreate = () => {
    setEditingAlias(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (idp: IdpRow) => {
    setEditingAlias(idp.alias);
    setForm({
      alias: idp.alias,
      displayName: idp.displayName,
      providerType: idp.providerType,
      tenantId: idp.tenantId ?? '',
      clientId: idp.clientId ?? '',
      clientSecret: idp.clientSecret ?? '',
      discoveryUrl: idp.discoveryUrl ?? '',
      enabled: idp.enabled,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editingAlias) {
        await apiClient.updateIdentityProvider(editingAlias, {
          displayName: form.displayName,
          tenantId: form.tenantId || undefined,
          clientId: form.clientId || undefined,
          clientSecret: form.clientSecret || undefined,
          discoveryUrl: form.discoveryUrl || undefined,
          enabled: form.enabled,
        });
      } else {
        await apiClient.createIdentityProvider({
          alias: form.alias.trim(),
          displayName: form.displayName.trim(),
          providerType: form.providerType,
          tenantId: form.tenantId || undefined,
          clientId: form.clientId || undefined,
          clientSecret: form.clientSecret || undefined,
          discoveryUrl: form.discoveryUrl || undefined,
        });
      }
      setDialogOpen(false);
      await fetchIdps();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save identity provider');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (alias: string) => {
    try {
      await apiClient.deleteIdentityProvider(alias);
      setConfirmDeleteAlias(null);
      await fetchIdps();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete identity provider');
    }
  };

  if (!canView) {
    return (
      <div className="p-6">
        <PageHeader
          icon={KeyRound}
          iconColor="text-violet-600 dark:text-violet-400"
          iconBgColor="bg-violet-100 dark:bg-violet-900/30"
          description="Manage SSO identity providers via Keycloak."
        />
        <p className="text-gray-500 dark:text-gray-400">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        icon={KeyRound}
        iconColor="text-violet-600 dark:text-violet-400"
        iconBgColor="bg-violet-100 dark:bg-violet-900/30"
        description="Configure upstream SSO providers (Azure Entra, OIDC) brokered through Keycloak."
        count={idps.length}
        countLabel="providers"
        countColor="text-violet-600 dark:text-violet-400"
        actions={
          canManage ? (
            <div className="w-full sm:w-auto sm:h-full flex items-stretch">
              <Button onClick={openCreate} className="w-full h-full">
                <Plus className="w-4 h-4 mr-2" />
                Add provider
              </Button>
            </div>
          ) : undefined
        }
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300 px-4 py-3">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Brokered providers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : idps.length === 0 ? (
            <div className="text-center py-10 text-gray-500 dark:text-gray-400">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No identity providers configured yet.</p>
              {canManage && (
                <Button className="mt-4" variant="outline" onClick={openCreate}>
                  Add your first provider
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-4">Alias</th>
                    <th className="py-2 pr-4">Display name</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4">Status</th>
                    {canManage && <th className="py-2 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {idps.map((idp) => (
                    <tr key={idp.alias} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-3 pr-4 font-mono text-xs">{idp.alias}</td>
                      <td className="py-3 pr-4">{idp.displayName}</td>
                      <td className="py-3 pr-4">
                        <Badge variant="secondary">{idp.providerType}</Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant={idp.enabled ? 'success' : 'secondary'}>
                          {idp.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </td>
                      {canManage && (
                        <td className="py-3 text-right space-x-2">
                          <Button size="sm" variant="outline" onClick={() => openEdit(idp)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          {confirmDeleteAlias === idp.alias ? (
                            <InlineConfirmation
                              message="Delete?"
                              onConfirm={() => void handleDelete(idp.alias)}
                              onCancel={() => setConfirmDeleteAlias(null)}
                            />
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setConfirmDeleteAlias(idp.alias)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAlias ? 'Edit identity provider' : 'Add identity provider'}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Provider credentials are stored as vault references (<span className="font-mono">:secret-name</span>).
              Values are resolved from the portal secrets store when syncing to Keycloak.
            </p>
            {!editingAlias && (
              <div>
                <label className="block text-sm font-medium mb-1">Alias</label>
                <Input
                  value={form.alias}
                  onChange={(e) => setForm({ ...form, alias: e.target.value })}
                  placeholder="entra-contoso"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Display name</label>
              <Input
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder="Contoso Azure AD"
              />
            </div>
            {!editingAlias && (
              <div>
                <label className="block text-sm font-medium mb-1">Provider type</label>
                <select
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                  value={form.providerType}
                  onChange={(e) => setForm({ ...form, providerType: e.target.value as ProviderType })}
                >
                  <option value="azure-entra">Azure Entra ID</option>
                  <option value="oidc">Generic OIDC</option>
                </select>
              </div>
            )}
            {form.providerType === 'azure-entra' && (
              <div>
                <label className="block text-sm font-medium mb-1">Azure tenant ID</label>
                <Input
                  value={form.tenantId}
                  onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
                  placeholder=":portal-azure-tenant-id"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{vaultRefHint}</p>
              </div>
            )}
            {form.providerType === 'oidc' && (
              <div>
                <label className="block text-sm font-medium mb-1">Discovery URL</label>
                <Input
                  value={form.discoveryUrl}
                  onChange={(e) => setForm({ ...form, discoveryUrl: e.target.value })}
                  placeholder=":my-oidc-discovery-url"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{vaultRefHint}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Client ID</label>
              <Input
                value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                placeholder=":portal-azure-client-id"
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{vaultRefHint}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Client secret</label>
              <Input
                value={form.clientSecret}
                onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
                placeholder=":portal-azure-client-secret"
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{vaultRefHint}</p>
            </div>
            {editingAlias && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
                Enabled
              </label>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
